import type { ListLeadsParams } from "../client.js";
import type { SortDir, SortKey } from "../components/LeadsTable.js";

/**
 * The operator-facing ordering of the Leads list.
 *
 * Why this exists: a `LeadsTable` column click can only re-order the ~50 rows it
 * was handed, so on its own it can't pull a lead off "page 33" to the front. So
 * EVERY clickable column is wired to a SERVER-side `sort` (forwarded to the
 * agent's `/v2/leads`) that orders the WHOLE dataset and resets to page 1 — the
 * column you click sorts the entire database, not just the page you're looking
 * at (2026-06-26). The table still applies the same key within the page, so the
 * visible order mirrors the server order (with deadline-urgency still floating
 * to the top of each page — see `sortLeads.ts`).
 *
 * The single source of truth is the table's `{ key: SortKey; dir: SortDir }`.
 * The "Sort" dropdown is a set of quick-pick presets that write the same state;
 * it also reflects whatever ordering a column click produced (`describeSort`).
 */
export type LeadSort = { key: SortKey; dir: SortDir };

/** The default ordering — "Most urgent" = heat desc (hottest first), a real
 * server-side order spanning every page. The Leads page keeps the **Today**
 * filter's own newest-first order when this default is untouched (see
 * `LeadsPage`), so only an explicit non-default choice overrides Today. */
export const DEFAULT_LEAD_SORT: LeadSort = { key: "heat", dir: "desc" };

/**
 * Maps a table `SortKey` to the server `sort` value the agent understands.
 * Every column — INCLUDING `heat` — is a real server-side order, so a column
 * click orders the whole DB, not just the visible page. `heat` orders on the
 * heat TIER (agent ranks cold < warm < hot < on_fire; see `_HEAT_RANK_ORDER`
 * there), which is the dashboard's default "most chase-worthy lead is row 1"
 * order — now spanning every page. (`null` is reserved for a future computed
 * key with no server order; none exist today.)
 */
const SERVER_SORT_COLUMN: Record<SortKey, string | null> = {
  heat: "heat",
  name: "name",
  country: "country",
  vertical: "vertical",
  source: "source",
  confidence: "confidence",
  created: "created_at",
  last: "last_signal_at",
};

/**
 * The `sort` (+ `sort_dir`) query params a chosen ordering contributes to the
 * leads list query. Every key (incl. `heat`) yields a real server-side order
 * that spans every page. The Leads page decides WHETHER to apply it on the
 * "Today" filter (it keeps Today's newest-first order until the operator picks a
 * non-default ordering); "Closing soon" is agent-enforced (deadline asc) and
 * ignores `sort` regardless.
 */
export function leadSortParams(
  sort: LeadSort,
): Pick<ListLeadsParams, "sort" | "sort_dir"> {
  const col = SERVER_SORT_COLUMN[sort.key];
  return col ? { sort: col, sort_dir: sort.dir } : {};
}

/** Quick-pick presets shown in the "Sort" dropdown. Each writes the unified
 * `{ key, dir }` state — the same state a column-header click sets. */
export interface LeadSortPreset {
  label: string;
  sort: LeadSort;
}
export const LEAD_SORT_PRESETS: LeadSortPreset[] = [
  { label: "Most urgent", sort: { key: "heat", dir: "desc" } },
  { label: "Newest first", sort: { key: "created", dir: "desc" } },
  { label: "Highest confidence", sort: { key: "confidence", dir: "desc" } },
];

export function sameSort(a: LeadSort, b: LeadSort): boolean {
  return a.key === b.key && a.dir === b.dir;
}

/**
 * A human label for ANY active sort, for the dropdown's "Sort: <…>" trigger —
 * so it stays in sync even when a column click produced an ordering that isn't
 * one of the three presets (e.g. "Country (A–Z)"). Heat collapses to "Most
 * urgent" in both directions (its server order is direction-independent).
 */
export function describeSort(sort: LeadSort): string {
  const preset = LEAD_SORT_PRESETS.find((p) => sameSort(p.sort, sort));
  if (preset) return preset.label;
  const asc = sort.dir === "asc";
  switch (sort.key) {
    case "name":
      return asc ? "Organisation (A–Z)" : "Organisation (Z–A)";
    case "country":
      return asc ? "Country (A–Z)" : "Country (Z–A)";
    case "vertical":
      return asc ? "Vertical (A–Z)" : "Vertical (Z–A)";
    case "source":
      return asc ? "Found by (A–Z)" : "Found by (Z–A)";
    case "confidence":
      return asc ? "Lowest confidence" : "Highest confidence";
    case "created":
      return asc ? "Oldest first" : "Newest first";
    case "last":
      return asc ? "Earliest signal" : "Latest signal";
    case "heat":
      return asc ? "Coldest first" : "Most urgent";
  }
}
